import { Close, UploadFile, Image, Description, Warning } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import LoadingButton from "../../../components/LoadingButton";
import PopOutTextInput from "../../../components/PopOutTextInput";
import { hostname, parseError } from "../../../utils";
import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  DOCUMENT_ACCEPT_TYPES,
  DOCUMENT_TYPE_LABELS,
  MAX_DOCUMENT_SIZE_MB,
} from "../../../constants";

export interface SupportTicket {
  id: string;
  companyId: string;
  userId: string;
  whitelabelId?: string;
  subject: string;
  description: string;
  severity: "low" | "medium" | "high" | "critical";
  status: "open" | "in_progress" | "resolved" | "closed";
  ticketType: "bug" | "feature_request" | "question" | "other";
  attachments: string[];
  response?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
  resolverEmail?: string;
  resolverName?: string;
  companyName?: string;
  whitelabelName?: string;
}

interface TicketFormDialogProps {
  open: boolean;
  onClose: () => void;
  ticket?: SupportTicket | null;
  companies: any[];
  getToken: () => Promise<string>;
  onSave: () => void;
  onError: (error: string) => void;
}

// Attachment types
interface AttachmentInfo {
  id: string;
  name: string;
  type: "image" | "document";
  previewUrl?: string;
}

// Allowed image extensions for screenshots
const ALLOWED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp"];
const MAX_IMAGE_SIZE_MB = 5;

// Combined accept types for file input
const COMBINED_ACCEPT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  DOCUMENT_ACCEPT_TYPES,
].join(",");

const TicketFormDialog: React.FC<TicketFormDialogProps> = ({
  open,
  onClose,
  ticket,
  companies,
  getToken,
  onSave,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    severity: "medium" as SupportTicket["severity"],
    ticketType: "question" as SupportTicket["ticketType"],
    companyId: "",
    attachments: [] as string[],
  });
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachmentInfos, setAttachmentInfos] = useState<AttachmentInfo[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        subject: ticket.subject,
        description: ticket.description,
        severity: ticket.severity,
        ticketType: ticket.ticketType,
        companyId: ticket.companyId || "",
        attachments: ticket.attachments || [],
      });
      // For existing tickets, we don't have the attachment info locally
      setAttachmentInfos([]);
    } else {
      setFormData({
        subject: "",
        description: "",
        severity: "medium",
        ticketType: "question",
        companyId: "",
        attachments: [],
      });
      setAttachmentInfos([]);
    }
    setIsDragOver(false);
    setShowConfirmDialog(false);
  }, [ticket, open]);

  const handleSubmitClick = () => {
    if (!formData.subject || !formData.description) {
      onError("Subject and description are required");
      return;
    }

    // Show confirmation if no attachments and creating new ticket
    if (!ticket && formData.attachments.length === 0) {
      setShowConfirmDialog(true);
      return;
    }

    handleSubmit();
  };

  const handleSubmit = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    try {
      const token = await getToken();
      const url = ticket
        ? `${hostname}/support/tickets/${ticket.id}`
        : `${hostname}/support/tickets`;
      const method = ticket ? "PUT" : "POST";

      await axios({
        method,
        url,
        data: { ...formData, companyId: formData.companyId || null },
        headers: { Authorization: `Bearer ${token}` },
      });

      onSave();
      onClose();
    } catch (err) {
      onError(parseError(err));
    }
    setLoading(false);
  };

  // Helper to determine if a file is an image
  const isImageFile = (file: File): boolean => {
    return file.type.startsWith("image/");
  };

  // Helper to validate file extension
  const validateFileExtension = (file: File): { valid: boolean; isImage: boolean } => {
    const fileName = file.name.toLowerCase();

    // Check if it's an image
    if (ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext))) {
      return { valid: true, isImage: true };
    }

    // Check if it's a document
    if (ALLOWED_DOCUMENT_EXTENSIONS.some((ext) => fileName.endsWith(ext))) {
      return { valid: true, isImage: false };
    }

    return { valid: false, isImage: false };
  };

  // Helper to get file extension label
  const getFileExtensionLabel = (fileName: string): string => {
    const ext = "." + fileName.split(".").pop()?.toLowerCase();
    return (
      DOCUMENT_TYPE_LABELS[ext as keyof typeof DOCUMENT_TYPE_LABELS] ||
      ext.toUpperCase().replace(".", "")
    );
  };

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    setUploadingAttachments(true);
    try {
      const token = await getToken();
      const newAttachmentIds: string[] = [];
      const newAttachmentInfos: AttachmentInfo[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = validateFileExtension(file);

        // Validate file type
        if (!validation.valid) {
          const supportedTypes = [
            ...ALLOWED_IMAGE_EXTENSIONS,
            ...ALLOWED_DOCUMENT_EXTENSIONS,
          ].join(", ");
          onError(
            `File "${file.name}" is not supported. Supported formats: ${supportedTypes}`
          );
          continue;
        }

        // Validate file size based on type
        const maxSize = validation.isImage ? MAX_IMAGE_SIZE_MB : MAX_DOCUMENT_SIZE_MB;
        if (file.size > maxSize * 1024 * 1024) {
          onError(
            `File "${file.name}" is too large. Maximum size: ${maxSize}MB.`
          );
          continue;
        }

        try {
          if (validation.isImage) {
            // Upload image to platform-images as private
            const imageFormData = new FormData();
            imageFormData.append("image", file);
            imageFormData.append("name", `Support Screenshot - ${file.name}`);
            imageFormData.append("category", "support-screenshot");
            imageFormData.append("public", "false");

            const uploadResponse = await axios.post(
              `${hostname}/platform-images`,
              imageFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const imageId = uploadResponse.data.id;

              // Fetch the private image to get a blob URL for preview
              const imageResponse = await axios.get(
                `${hostname}/platform-images/pr/${imageId}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                }
              );
              const blob = new Blob([imageResponse.data], {
                type: imageResponse.headers["content-type"],
              });
              const previewUrl = window.URL.createObjectURL(blob);

              newAttachmentIds.push(imageId);
              newAttachmentInfos.push({
                id: imageId,
                name: file.name,
                type: "image",
                previewUrl: previewUrl,
              });
            }
          } else {
            // Upload document to support-documents endpoint
            const docFormData = new FormData();
            docFormData.append("file", file);
            docFormData.append("name", file.name);

            const uploadResponse = await axios.post(
              `${hostname}/support/documents`,
              docFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const docId = uploadResponse.data.id;

              newAttachmentIds.push(docId);
              newAttachmentInfos.push({
                id: docId,
                name: file.name,
                type: "document",
              });
            }
          }
        } catch (fileErr) {
          onError(`Failed to upload "${file.name}": ${parseError(fileErr)}`);
          // Continue with other files
        }
      }

      // Update form data with new attachments
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachmentIds],
      }));
      setAttachmentInfos((prev) => [...prev, ...newAttachmentInfos]);
    } catch (err) {
      onError(`Failed to upload file: ${parseError(err)}`);
    }
    setUploadingAttachments(false);
  };

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      await handleFileUpload(files);
    }
    // Clear file input
    event.target.value = "";
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!uploadingAttachments) {
      setIsDragOver(true);
    }
  }, [uploadingAttachments]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (uploadingAttachments) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        await handleFileUpload(files);
      }
    },
    [uploadingAttachments, getToken, onError]
  );

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
    setAttachmentInfos((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {ticket ? "Edit Support Ticket" : "New Support Ticket"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              required
            />
          </Grid>
          <Grid item xs={12}>
            <PopOutTextInput
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(value) =>
                setFormData({ ...formData, description: value })
              }
              multiline
              rows={4}
              required
              dialogTitle="Edit Ticket Description"
              dialogDescription="Provide a detailed description of the issue or request."
              popOutRows={15}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Company (Optional)</InputLabel>
              <Select
                value={formData.companyId}
                onChange={(e) =>
                  setFormData({ ...formData, companyId: e.target.value })
                }
                label="Company (Optional)"
              >
                <MenuItem value="">
                  <em>None - General Bug/Issue</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    severity: e.target.value as SupportTicket["severity"],
                  })
                }
                label="Severity"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.ticketType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ticketType: e.target.value as SupportTicket["ticketType"],
                  })
                }
                label="Type"
              >
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="feature_request">Feature Request</MenuItem>
                <MenuItem value="question">Question</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Attachments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload screenshots or documents to help explain the issue.
                Supports images (PNG, JPG, GIF, max 5MB) and documents (PDF,
                Word, Excel, CSV, text files, max {MAX_DOCUMENT_SIZE_MB}MB).
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mb: 2,
                  border: "2px dashed",
                  borderColor: isDragOver ? "primary.main" : "divider",
                  backgroundColor: isDragOver ? "action.selected" : "transparent",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <UploadFile
                  sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {isDragOver
                    ? "Drop your files here"
                    : "Drag and drop files here"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  or
                </Typography>
                <input
                  type="file"
                  accept={COMBINED_ACCEPT_TYPES}
                  multiple
                  onChange={handleInputChange}
                  style={{ display: "none" }}
                  id="attachment-upload-input"
                />
                <label htmlFor="attachment-upload-input">
                  <LoadingButton
                    component="span"
                    variant="outlined"
                    startIcon={<UploadFile />}
                    loading={uploadingAttachments}
                  >
                    {uploadingAttachments ? "Uploading..." : "Choose Files"}
                  </LoadingButton>
                </label>
              </Paper>

              {attachmentInfos.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Uploaded Files ({attachmentInfos.length}):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {attachmentInfos.map((attachment, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          width: attachment.type === "image" ? 100 : "auto",
                          minWidth: attachment.type === "document" ? 150 : 100,
                          height: 100,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            attachment.type === "document"
                              ? "action.hover"
                              : "transparent",
                          p: attachment.type === "document" ? 1 : 0,
                        }}
                      >
                        {attachment.type === "image" && attachment.previewUrl ? (
                          <img
                            src={attachment.previewUrl}
                            alt={attachment.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <>
                            <Description
                              sx={{ fontSize: 32, color: "primary.main", mb: 0.5 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                textAlign: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                px: 0.5,
                              }}
                              title={attachment.name}
                            >
                              {attachment.name.length > 15
                                ? attachment.name.substring(0, 12) + "..."
                                : attachment.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {getFileExtensionLabel(attachment.name)}
                            </Typography>
                          </>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => removeAttachment(index)}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bgcolor: "background.paper",
                            opacity: 0.9,
                            "&:hover": {
                              bgcolor: "background.paper",
                              opacity: 1,
                            },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleSubmitClick}
          variant="contained"
        >
          {ticket ? "Update" : "Create"}
        </LoadingButton>
      </DialogActions>

      {/* Confirmation Dialog for missing attachments */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="warning" />
          No Attachments Included
        </DialogTitle>
        <DialogContent>
          <Typography>
            You haven't attached any screenshots or documents to this ticket.
            Including screenshots or relevant files helps us understand and
            resolve your issue faster.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Are you sure you want to submit without attachments?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Add Attachments
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TicketFormDialog;
